import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Select,
  Option,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import api from "../../lib/api";
import type { QRCodeRecord, QRCodeType } from "../../types/marketing";
import { createQR, exportQR, contrastRatio } from "./qrRendering";
import toast from "react-hot-toast";

const types: QRCodeType[] = [
  "website",
  "review_link",
  "call",
  "sms",
  "email",
  "payment",
  "whatsapp",
  "funnel",
  "form",
  "survey",
  "quiz",
  "profile_card",
  "vcard",
  "business_card",
];
const defaults = {
  name: "",
  qr_type: "website",
  url: "",
  phone: "",
  email: "",
  message: "",
  background_color: "#ffffff",
  dots_color: "#000000",
  marker_border_color: "#000000",
  marker_center_color: "#000000",
  dots_style: "square",
  marker_border_style: "square",
  marker_center_style: "dot",
  qr_shape: "square",
  frame_style: "none",
  logo_image: null,
  status: "active",
  campaign: "",
  label: "",
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
};
type Draft = Omit<typeof defaults, "logo_image"> & {
  logo_image: number | null;
};
function errorMessage(cause: unknown) {
  const body = (cause as { responseData?: Record<string, unknown> })
    ?.responseData;
  if (body)
    return Object.entries(body)
      .map(
        ([field, value]) =>
          `${field}: ${Array.isArray(value) ? value.join(", ") : String(value)}`,
      )
      .join("; ");
  return cause instanceof Error
    ? cause.message
    : "Unable to save. Please retry.";
}
const editable = (record: QRCodeRecord): Draft =>
  Object.fromEntries(
    Object.keys(defaults).map((key) => [
      key,
      record[key as keyof QRCodeRecord] ?? defaults[key as keyof Draft],
    ]),
  ) as Draft;
const presets = [
  {
    name: "Yard sign",
    label: "Scan for a roof quote",
    frame_style: "frame",
    dots_style: "square",
  },
  {
    name: "Truck decal",
    label: "Your local roofing team",
    frame_style: "border",
    dots_style: "rounded",
  },
  {
    name: "Business card",
    label: "Let’s talk roofing",
    frame_style: "none",
    dots_style: "classy",
  },
  {
    name: "Review request",
    label: "Tell us how we did",
    frame_style: "frame",
    dots_style: "dots",
  },
  {
    name: "Direct mail",
    label: "Book your roof inspection",
    frame_style: "border",
    dots_style: "extra-rounded",
  },
];
export function destinationError(draft: Draft) {
  if (!draft.name.trim()) return "Enter a name for this code.";
  if (["call", "sms", "whatsapp"].includes(draft.qr_type))
    return /^\+?[\d ()\-.]{7,25}$/.test(draft.phone) &&
      draft.phone.replace(/\D/g, "").length >= 7
      ? ""
      : "Enter a phone number including country code.";
  if (draft.qr_type === "email")
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email)
      ? ""
      : "Enter a valid email address.";
  if (
    draft.qr_type === "vcard" &&
    !draft.url &&
    draft.message.startsWith("BEGIN:VCARD") &&
    draft.message.trimEnd().endsWith("END:VCARD")
  )
    return "";
  try {
    const url = new URL(draft.url);
    if (["https:", "http:"].includes(url.protocol)) return "";
  } catch {
    /* Validation below */
  }
  return "Enter a complete HTTP or HTTPS destination URL.";
}

export const CreateQRCodeModal: React.FC<{
  open: boolean;
  onClose: () => void;
  editingQRCode: QRCodeRecord | null;
}> = ({ open, onClose, editingQRCode }) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>({ ...defaults });
  const [saved, setSaved] = useState<QRCodeRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const preview = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) {
      setDraft(editingQRCode ? editable(editingQRCode) : { ...defaults });
      setSaved(editingQRCode);
      setError("");
    }
  }, [open, editingQRCode]);
  const { data: media = [], isError: mediaError } = useQuery({
    queryKey: ["qr-logo-media"],
    enabled: open,
    queryFn: async () => {
      const rows: { id: number; name: string; mime_type: string }[] = [];
      for (let page = 1; ; page++) {
        const response = await api.get<{
          results: typeof rows;
          next: string | null;
        }>(`/api/marketing/media/?page=${page}`);
        rows.push(
          ...response.data.results.filter((item) =>
            item.mime_type?.startsWith("image/"),
          ),
        );
        if (!response.data.next) return rows;
      }
    },
  });
  const { data: org } = useQuery({
    queryKey: ["qr-brand"],
    enabled: open,
    queryFn: async () =>
      (
        await api.get<{ primary_color?: string }>(
          "/api/marketing/qr-codes/branding/",
        )
      ).data,
  });
  useEffect(() => {
    if (!open || !saved || !preview.current) return;
    const element = preview.current;
    element.replaceChildren();
    let active = true;
    try {
      const qr = createQR(saved);
      qr.getRawData("svg")
        .then(() => {
          if (active) qr.append(element);
        })
        .catch(() => {
          if (active)
            setError(
              "Preview could not load. Check the logo and try saving again.",
            );
        });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to preview.");
    }
    return () => {
      active = false;
    };
  }, [saved, open]);
  const change = (key: keyof Draft, value: unknown) =>
    setDraft((previous) => ({ ...previous, [key]: value }));
  const dirty =
    !saved || JSON.stringify(draft) !== JSON.stringify(editable(saved));
  const invalid = destinationError(draft);
  const ratio = Math.min(
    ...[
      draft.dots_color,
      draft.marker_border_color,
      draft.marker_center_color,
    ].map((color) => contrastRatio(color, draft.background_color)),
  );
  const save = async () => {
    if (invalid) {
      setError(invalid);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = saved
        ? await api.patch<QRCodeRecord>(
            `/api/marketing/qr-codes/${saved.id}/`,
            draft,
          )
        : await api.post<QRCodeRecord>("/api/marketing/qr-codes/", draft);
      setSaved(result.data);
      setDraft(editable(result.data));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["qr-codes"] }),
        queryClient.invalidateQueries({ queryKey: ["qr-codes-stats"] }),
      ]);
      toast.success("Saved. Preview and downloads now use the saved code.");
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };
  const download = async (format: "png" | "pdf") => {
    if (!saved || dirty) return;
    setBusy(true);
    setError("");
    try {
      const fresh = (
        await api.get<QRCodeRecord>(`/api/marketing/qr-codes/${saved.id}/`)
      ).data;
      await exportQR(fresh, format);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Export failed. Please retry.",
      );
    } finally {
      setBusy(false);
    }
  };
  const input = (key: keyof Draft, label: string, placeholder?: string) => (
    <FormControl key={key}>
      <FormLabel>{label}</FormLabel>
      <Input
        disabled={busy}
        value={String(draft[key] ?? "")}
        onChange={(event) => change(key, event.target.value)}
        placeholder={placeholder}
      />
    </FormControl>
  );
  const select = (key: keyof Draft, label: string, values: string[]) => (
    <FormControl key={key}>
      <FormLabel>{label}</FormLabel>
      <Select
        disabled={busy}
        value={String(draft[key])}
        onChange={(_, value) => change(key, value)}
      >
        {values.map((value) => (
          <Option value={value} key={value}>
            {value.replaceAll("_", " ")}
          </Option>
        ))}
      </Select>
    </FormControl>
  );
  return (
    <Modal
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <ModalDialog
        sx={{
          width: "95vw",
          maxWidth: 1100,
          maxHeight: "92vh",
          overflow: "auto",
        }}
      >
        <ModalClose disabled={busy} />
        <Typography level="h4">
          {saved ? "Edit QR code" : "Create QR code"}
        </Typography>
        {error && <Alert color="danger">{error}</Alert>}
        <Grid container spacing={3}>
          <Grid xs={12} md={7}>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Branded preset</FormLabel>
                <Select
                  disabled={busy}
                  placeholder="Choose a print use"
                  onChange={(_, name) => {
                    const preset = presets.find((item) => item.name === name);
                    if (preset)
                      setDraft((previous) => ({
                        ...previous,
                        ...preset,
                        name: previous.name || preset.name,
                        dots_color: /^#[\da-f]{6}$/i.test(
                          org?.primary_color || "",
                        )
                          ? org!.primary_color!
                          : "#17324d",
                        marker_border_color: "#17324d",
                        marker_center_color: "#17324d",
                      }));
                  }}
                >
                  {presets.map((preset) => (
                    <Option key={preset.name} value={preset.name}>
                      {preset.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              {input("name", "Name")}
              {select("qr_type", "Destination type", types)}
              {["call", "sms", "whatsapp"].includes(draft.qr_type)
                ? input(
                    "phone",
                    "Phone number with country code",
                    "+15551234567",
                  )
                : draft.qr_type === "email"
                  ? input("email", "Email address")
                  : input(
                      "url",
                      draft.qr_type === "vcard"
                        ? "Hosted vCard URL (or enter raw vCard below)"
                        : "Destination URL",
                      "https://example.com",
                    )}
              {["sms", "email", "whatsapp", "vcard"].includes(
                draft.qr_type,
              ) && (
                <FormControl>
                  <FormLabel>
                    {draft.qr_type === "email"
                      ? "Subject"
                      : draft.qr_type === "vcard"
                        ? "Raw vCard content (when URL is blank)"
                        : "Message"}
                  </FormLabel>
                  <Textarea
                    disabled={busy}
                    value={draft.message}
                    onChange={(e) => change("message", e.target.value)}
                  />
                </FormControl>
              )}
              {saved &&
                (draft.qr_type !== saved.qr_type ||
                  draft.url !== saved.url ||
                  draft.phone !== saved.phone ||
                  draft.email !== saved.email ||
                  draft.message !== saved.message) && (
                  <Alert color="warning">
                    Changing an HTTP destination updates existing printed links.
                    Changing the type, or changing a call, SMS, email, or raw
                    vCard, requires downloading and reprinting the code.
                  </Alert>
                )}
              {input("label", "Printed caption")}
              {input("campaign", "Campaign label")}
              {!["call", "sms", "email", "vcard"].includes(draft.qr_type) && (
                <Stack spacing={1}>
                  {input("utm_source", "UTM source", "yard-sign")}
                  {input("utm_medium", "UTM medium", "qr")}
                  {input("utm_campaign", "UTM campaign", "fall-inspections")}
                </Stack>
              )}
              <Grid container spacing={1}>
                {(
                  [
                    "background_color",
                    "dots_color",
                    "marker_border_color",
                    "marker_center_color",
                  ] as const
                ).map((key) => (
                  <Grid xs={12} sm={6} key={key}>
                    {input(key, key.replaceAll("_", " "), "#123456")}
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={1}>
                {(
                  [
                    [
                      "dots_style",
                      "Dot style",
                      [
                        "square",
                        "dots",
                        "rounded",
                        "classy",
                        "classy-rounded",
                        "extra-rounded",
                      ],
                    ],
                    [
                      "marker_border_style",
                      "Marker border",
                      ["square", "circle", "rounded"],
                    ],
                    ["marker_center_style", "Marker center", ["dot", "square"]],
                    [
                      "qr_shape",
                      "Background shape",
                      ["square", "rounded", "circle"],
                    ],
                    ["frame_style", "Frame", ["none", "border", "frame"]],
                  ] as [keyof Draft, string, string[]][]
                ).map(([key, label, values]) => (
                  <Grid xs={12} sm={6} key={key}>
                    {select(key, label, values)}
                  </Grid>
                ))}
              </Grid>
              <FormControl>
                <FormLabel>Logo from your media library</FormLabel>
                <Select
                  disabled={busy}
                  value={draft.logo_image || 0}
                  onChange={(_, value) => change("logo_image", value || null)}
                >
                  <Option value={0}>No logo</Option>
                  {media.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
                {mediaError && (
                  <Typography color="warning">
                    Media could not load. Reopen this editor to retry.
                  </Typography>
                )}
              </FormControl>
            </Stack>
          </Grid>
          <Grid xs={12} md={5}>
            <Card sx={{ position: { md: "sticky" }, top: 0 }}>
              <Typography level="title-lg">Saved print preview</Typography>
              {dirty && (
                <Alert color="warning">
                  Unsaved changes. Save to refresh the preview and enable
                  downloads.
                </Alert>
              )}
              {saved ? (
                <Box
                  ref={preview}
                  sx={{
                    mx: "auto",
                    width: "100%",
                    maxWidth: 280,
                    "& svg": { maxWidth: "100%", height: "auto" },
                  }}
                />
              ) : (
                <Typography>
                  Complete the destination and save to generate your trackable
                  code.
                </Typography>
              )}
              {saved && (
                <Typography level="body-sm" sx={{ overflowWrap: "anywhere" }}>
                  {saved.destination_url}
                </Typography>
              )}
              <Alert color={ratio >= 4.5 ? "success" : "warning"}>
                Minimum color contrast: {ratio.toFixed(1)}:1. Aim for at least
                4.5:1 with dark dots on a light background.
              </Alert>
              <Typography level="body-sm">
                Keep the built-in quiet zone clear. Start at 1 inch (2.5 cm) for
                close-range scanning; use larger codes for signs and vehicles.
                Test a printed sample from the intended viewing distance.
              </Typography>
              <Typography level="body-sm">
                Scan this saved preview with your phone before printing.
                Previewing here does not record a scan; scanning a tracked link
                does.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  disabled={!saved || dirty || busy}
                  onClick={() => download("png")}
                >
                  PNG
                </Button>
                <Button
                  disabled={!saved || dirty || busy}
                  onClick={() => download("pdf")}
                >
                  Scalable PDF
                </Button>
              </Stack>
            </Card>
          </Grid>
        </Grid>
        {invalid && <Typography color="warning">{invalid}</Typography>}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="plain" disabled={busy} onClick={onClose}>
            Close
          </Button>
          <Button loading={busy} disabled={!!invalid || !dirty} onClick={save}>
            Save & preview
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};
